import {
  Alert,
  Button,
  CloseButton,
  Flex,
  Heading,
  IconAddLine,
  List,
  ScreenReaderContent,
  Spinner,
  Tabs,
  Text,
  TextInput,
  Tray,
  View,
} from "@instructure/ui";
import { t } from "i18next";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalService } from "../../../services/ExternalService";
import { ERROR, useNotification } from "../../CustomHooks/NotificationProvider";
import Student from "./Student";
import UploadFileModal from "./UploadFileModal";

const RosterManagementTray = ({
  rosterFileDrop,
  shouldOpen,
  handleSubmit,
  handleCancel,
  uploadError,
  students,
  setStudents,
  contextId,
  raterData,
  setRaterData,
  user,
  isGradingComplete,
  setIsGradingComplete,
  submittingRoster,
  locationState,
  sortByLastName,
}) => {
  const showNotification = useNotification();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [errFlag, setErrFlag] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadCanvasId, setUploadCanvasId] = useState(null);
  const [studentEvidenceName, setStudentEvidenceName] = useState(null);
  const [resultsMap, setResultsMap] = useState({});
  const studentId = useRef();

  useEffect(() => {
    setLoading(true);
    let obj = {};
    if (Object.keys(raterData).length > 0) {
      Object.keys(raterData).forEach((student) => {
        Object.keys(raterData[student]).forEach((outcome) => {
          obj[student] = raterData[student][outcome].score_finalized;
        });
      });
      setResultsMap(obj);
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [raterData]);

  const handleAddStudent = () => {
    if (studentId.current.value) {
      setLoading(true);
      ExternalService.addExternalUserToOffering(
        parseInt(studentId.current.value),
        contextId
      ).then((student) => {
        if (student) {
          if (!students.find((s) => s.ddl_id === student.ddl_id)) {
            setErrFlag("");
            setStudents([...students, student].sort(sortByLastName));
            if (Object.keys(raterData).length > 0) {
              let outcome_ids = [];
              let first_entry = "";

              const firstId = Object.keys(raterData)[0];
              outcome_ids = Object.keys(raterData[firstId]);
              first_entry = raterData[firstId];

              raterData[student.canvas_id] = {};

              outcome_ids.forEach((outcome_id) => {
                raterData[student.canvas_id][outcome_id] = {
                  mastery: false,
                  score: null,
                  possible: first_entry[outcome_id].possible,
                  percent: null,
                  submitted_or_assessed_at: "",
                  canvas_user_id: student.canvas_id,
                  canvas_learning_outcome_id:
                    first_entry[outcome_id].canvas_learning_outcome_id,
                  outcome_id: outcome_id,
                  evidence_id: "",
                  purl_assignment_id:
                    first_entry[outcome_id].purl_assignment_id,
                  assessor_id: user,
                  canvas_enrollment_term_id:
                    first_entry[outcome_id].canvas_enrollment_term_id,
                  aggregate_score: null,
                  score_finalized: false,
                  ddl_section_id: contextId,
                };
              });

              setRaterData({ ...raterData });

              window.localStorage.setItem(
                `raterData/${locationState.share_id}`,
                JSON.stringify(raterData)
              );
            }

            if (isGradingComplete) {
              setIsGradingComplete(false);
            }
            setLoading(false);
            studentId.current.value = "";
          } else {
            setErrFlag(t("rater.studentAlreadyInRoster"));
            setLoading(false);
          }
        } else {
          setErrFlag(t("rater.studentCanvasIdNotFound"));
          setLoading(false);
        }
      });
    } else {
      setErrFlag(t("rater.canvasIdRequired"));
    }
  };

  const handleUploadStudentEvidence = (student) => {
    setUploadCanvasId(student.canvas_id);
    setStudentEvidenceName(student.name);
  };

  const handleEvidenceCancel = () => {
    setUploadCanvasId(null);
    setStudentEvidenceName(null);
  };

  const handleSubmitEvidenceFiles = async (evidenceFiles) => {
    await ExternalService.saveFiles(
      evidenceFiles,
      "evidence",
      locationState.assessment_id + "/" + uploadCanvasId.toString(),
      "evidence.zip"
    )
      .then(() => {
        handleEvidenceCancel();
        showNotification(t("juriedAssessment.uploadEvidenceSuccess"));
      })
      .catch(() => {
        showNotification(t("notifications.general.uploadError"), ERROR);
      });
  };

  return (
    <>
      <Tray
        size="regular"
        label={t("rater.rosterManagement")}
        open={shouldOpen}
        placement="end"
      >
        <View as="div" padding="small">
          <Heading level="h2" as="h1" border="bottom" margin="0 0 small 0">
            {t("rater.rosterManagement")}
          </Heading>
          <Flex margin="0 0 small 0">
            <Flex.Item>
              <CloseButton
                placement="end"
                offset="small"
                screenReaderLabel="Close"
                onClick={handleCancel}
              />
            </Flex.Item>
          </Flex>
          <Tabs onRequestTabChange={(_e, { index }) => setSelectedIndex(index)}>
            <Tabs.Panel
              renderTitle="Roster View"
              isSelected={selectedIndex === 0}
            >
              <Flex alignItems="center" gap="small" margin="0 0 small 0">
                <Flex.Item shouldGrow>
                  <TextInput
                    type="text"
                    size="small"
                    placeholder={t("rater.canvasId")}
                    renderLabel={
                      <ScreenReaderContent>
                        {t("rater.addStudent")}
                      </ScreenReaderContent>
                    }
                    messages={errFlag ? [{ text: errFlag, type: "error" }] : []}
                    inputRef={(el) => (studentId.current = el)}
                  />
                </Flex.Item>
                <Flex.Item align="start">
                  <Button
                    size="small"
                    renderIcon={
                      loading ? (
                        <Spinner
                          renderTitle={t("global.pleaseWait")}
                          size="x-small"
                        />
                      ) : (
                        IconAddLine
                      )
                    }
                    disabled={loading}
                    onClick={handleAddStudent}
                  >
                    {t("rater.addStudent")}
                  </Button>
                </Flex.Item>
              </Flex>
              <List isUnstyled>
                {students.length > 0 ? (
                  students.map((student, index) => (
                    <List.Item id={index} key={index} margin="small 0">
                      <Student
                        contextId={contextId}
                        student={student}
                        students={students}
                        setStudents={setStudents}
                        raterData={raterData}
                        setRaterData={setRaterData}
                        resultsMap={resultsMap}
                        handleUploadStudentEvidence={
                          handleUploadStudentEvidence
                        }
                        shareId={locationState.share_id}
                        sortByLastName={sortByLastName}
                      />
                    </List.Item>
                  ))
                ) : (
                  <List.Item id={"no-students"}>
                    <Text>{t("rater.noStudents")}</Text>
                  </List.Item>
                )}
              </List>
            </Tabs.Panel>
            <Tabs.Panel
              renderTitle={t("rater.rosterUpload")}
              isSelected={selectedIndex === 1}
            >
              <View as="div">
                {rosterFileDrop}
                <div style={{ marginTop: 10 }}>
                  <Text as="div">
                    <Link to={"/sampleStudents.csv"} target="_blank">
                      {t("global.downloadExampleFile")}
                    </Link>
                  </Text>
                  <Text>{t("rater.rosterRequiredFields")}</Text>
                </div>
                {uploadError && (
                  <Alert
                    variant="error"
                    renderCloseButtonLabel={t("global.close")}
                    margin="small"
                  >
                    {t("learningOutcomes.uploadError")}
                  </Alert>
                )}
                <Flex justifyItems="end">
                  <Flex.Item>
                    <Button margin="small small 0 0" onClick={handleCancel}>
                      {t("global.cancel")}
                    </Button>
                    <Button
                      onClick={handleSubmit}
                      color="primary"
                      margin="small 0 0 0"
                      disabled={submittingRoster}
                    >
                      {submittingRoster ? (
                        <Spinner
                          size="x-small"
                          renderTitle={t("global.pleaseWait")}
                        />
                      ) : (
                        t("global.submit")
                      )}
                    </Button>
                  </Flex.Item>
                </Flex>
              </View>
            </Tabs.Panel>
          </Tabs>
        </View>
      </Tray>
      <UploadFileModal
        shouldOpen={uploadCanvasId}
        modalHeaderText={t("rater.uploadEvidenceFor") + studentEvidenceName}
        fileDropHeader={t("juriedAssessment.fileDrop")}
        handleCancel={handleEvidenceCancel}
        handleSubmit={handleSubmitEvidenceFiles}
        multiFileSupport={true}
        fileExtensions={
          ".csv,.txt,.xls,.xlsx,.pdf,.doc,.docx,.jpg,.jpeg,.png,.zip"
        }
        modalCopy={
          <Text as="div" lineHeight="double">
            {t("juriedAssessment.uploadInstructions")}
          </Text>
        }
      />
    </>
  );
};

export default RosterManagementTray;
